import type { JSX } from 'react';
import { useState } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useAssignTeacher,
  useClassTeachers,
  useRemoveTeacher,
  useTeacherRoster,
} from '@/features/classes/hooks';
import { ApiError } from '@/lib/api/client';
import { useHasCapability } from '@/lib/capabilities';

/**
 * Teacher assignment for one class (Phase G-6). There is no teacher table
 * (ADR-020) - `useTeacherRoster` lists users holding an active Teacher
 * membership in this organization, the only source a picker can draw from.
 * Assigning requires `teachers:manage`; the roster read and current
 * assignment list only require `teachers:read`, so every role that can
 * reach this class detail page sees who teaches it.
 */
export function ClassTeachersSection({ classId }: { readonly classId: string }): JSX.Element {
  const { data: teachers, isLoading, isError } = useClassTeachers(classId);
  const { data: roster } = useTeacherRoster();
  const canManage = useHasCapability('teachers:manage');
  const [selectedTeacherId, setSelectedTeacherId] = useState('');
  const assign = useAssignTeacher(classId);
  const remove = useRemoveTeacher(classId);

  const assignedIds = new Set((teachers ?? []).map((entry) => entry.teacherId));
  const availableTeachers = (roster?.items ?? []).filter(
    (teacher) => !assignedIds.has(teacher.userId),
  );

  function handleAssign(): void {
    if (selectedTeacherId.length === 0) {
      return;
    }
    assign.mutate(
      { teacherId: selectedTeacherId },
      {
        onSuccess: () => {
          setSelectedTeacherId('');
        },
      },
    );
  }

  const apiErrorMessage =
    assign.error instanceof ApiError
      ? assign.error.message
      : assign.error !== null
        ? 'Gagal menugaskan guru. Silakan coba lagi.'
        : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Guru</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {isLoading && (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        )}

        {isError && (
          <Alert variant="destructive">
            <AlertDescription>Gagal memuat daftar guru.</AlertDescription>
          </Alert>
        )}

        {!isLoading && !isError && teachers !== undefined && teachers.length === 0 && (
          <p className="text-sm text-muted-foreground">Belum ada guru yang ditugaskan.</p>
        )}

        {!isLoading && !isError && teachers !== undefined && teachers.length > 0 && (
          <ul className="flex flex-col gap-2">
            {teachers.map((entry) => (
              <li
                key={entry.id}
                className="flex flex-col gap-2 rounded-md border border-border p-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex min-w-0 flex-col">
                  <span className="truncate text-sm font-medium">{entry.teacher.fullName}</span>
                  <span className="truncate text-xs text-muted-foreground">
                    {entry.teacher.email}
                  </span>
                </div>
                {canManage && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button type="button" variant="outline" size="sm" disabled={remove.isPending}>
                        Lepas Tugas
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          Lepas tugas {entry.teacher.fullName} dari kelas ini?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          Guru tidak lagi mengampu kelas ini. Riwayat penugasan tetap tersimpan dan
                          guru dapat ditugaskan kembali kapan saja.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction
                          disabled={remove.isPending}
                          onClick={() => {
                            remove.mutate(entry.teacherId);
                          }}
                        >
                          Lepas Tugas
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </li>
            ))}
          </ul>
        )}

        {canManage && (
          <div className="flex flex-col gap-2 border-t border-border pt-4 sm:flex-row sm:items-end">
            <div className="flex flex-1 flex-col gap-2">
              <Select value={selectedTeacherId} onValueChange={setSelectedTeacherId}>
                <SelectTrigger id="assign-teacher" aria-label="Pilih guru">
                  <SelectValue placeholder="Pilih guru untuk ditugaskan" />
                </SelectTrigger>
                <SelectContent>
                  {availableTeachers.map((teacher) => (
                    <SelectItem key={teacher.userId} value={teacher.userId}>
                      {teacher.fullName} ({teacher.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="button"
              disabled={selectedTeacherId.length === 0 || assign.isPending}
              onClick={handleAssign}
            >
              {assign.isPending ? 'Menugaskan...' : 'Tugaskan'}
            </Button>
          </div>
        )}

        {apiErrorMessage !== null && (
          <Alert variant="destructive">
            <AlertDescription>{apiErrorMessage}</AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
